import React, { useState, useEffect, useMemo } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Activity, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import PulseBreakdownModal from '@/components/PulseBreakdownModal';
import GymDrilldownModal from '@/components/GymDrilldownModal';
import { FilterBar } from '@/components/pulse-operations/FilterBar';
import { SavedFilterViews } from '@/components/pulse-operations/SavedFilterViews';
import { PerformanceIndicators } from '@/components/pulse-operations/PerformanceIndicators';
import { useMultiDimensionalFilter } from '@/hooks/useMultiDimensionalFilter';
import { usePerformanceMetrics } from '@/hooks/usePerformanceMetrics';
import { cn } from '@/lib/utils';

export default function PulseOperations() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [drilldownMetric, setDrilldownMetric] = useState(null);
  const [pulseModalOpen, setPulseModalOpen] = useState(false);
  const [selectedPulse, setSelectedPulse] = useState(null);
  const [selectedGym, setSelectedGym] = useState(null);
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [keyMetrics, setKeyMetrics] = useState({ visit_scheduled: 0, visit_done: 0, closure_expected: 0, closure_done: 0 });
  const [filters, setFilters] = useState({
    tls: [],
    gyms: [],
    managers: [],
    sources: [],
    verificationStatuses: [],
    performanceLevels: [],
    customPerformanceRange: null,
    fdAvailability: [],
    commitmentStatuses: []
  });

  // Filter gyms to only show those with assigned TL and Manager
  const assignedGyms = useMemo(() => {
    return data.filter(g => g.tl_name && g.manager_name && g.tl_name !== 'Unassigned' && g.manager_name !== 'Unassigned');
  }, [data]);

  // Apply multi-dimensional filters
  const filteredData = useMultiDimensionalFilter(assignedGyms, filters);

  // Add performance indicators to filtered data
  const dataWithIndicators = usePerformanceMetrics(filteredData);

  // Sort by performance: problem gyms first, then hierarchical (Manager -> TL -> Gym)
  const sortedData = useMemo(() => {
    return [...dataWithIndicators].sort((a, b) => {
      // Problem gyms (red flags) sort to top
      const aHasProblems = a.indicators?.problems?.length > 0;
      const bHasProblems = b.indicators?.problems?.length > 0;

      if (aHasProblems && !bHasProblems) return -1;
      if (!aHasProblems && bHasProblems) return 1;

      // Within same performance tier, sort hierarchically: Manager -> TL -> Gym
      const managerCompare = (a.manager_name || '').localeCompare(b.manager_name || '');
      if (managerCompare !== 0) return managerCompare;

      const tlCompare = (a.tl_name || '').localeCompare(b.tl_name || '');
      if (tlCompare !== 0) return tlCompare;

      return (a.gym_name || '').localeCompare(b.gym_name || '');
    });
  }, [dataWithIndicators]);

  // Extract unique filter options from assigned gyms
  const filterOptions = useMemo(() => {
    const tls = [...new Set(assignedGyms.map(g => g.tl_name).filter(Boolean))].sort();
    const gyms = [...new Set(assignedGyms.map(g => g.gym_name).filter(Boolean))].sort();
    const managers = [...new Set(assignedGyms.map(g => g.manager_name).filter(Boolean))].sort();
    const sources = [...new Set(assignedGyms.map(g => g.source_name).filter(Boolean))].sort();
    const verificationStatuses = ['TL Verified', 'Manager Verified', 'Both Verified', 'Unverified'];
    const commitmentStatuses = [...new Set(assignedGyms.map(g => g.commitment_status).filter(Boolean))].sort();

    return { tls, gyms, managers, sources, verificationStatuses, commitmentStatuses };
  }, [assignedGyms]);

  const summaryMetrics = useMemo(() => {
    const totalGyms = sortedData.length;
    const p1Filled = sortedData.filter(g => g.p1_status === 'filled').length;
    const p2Filled = sortedData.filter(g => g.p2_status === 'filled').length;
    const p3Filled = sortedData.filter(g => g.p3_status === 'filled').length;
    const p4Filled = sortedData.filter(g => g.p4_status === 'filled').length;
    const pendingVerifications = sortedData.reduce((sum, g) => sum + (g.pending_verifications || 0), 0);
    const pendingCommitments = sortedData.reduce((sum, g) => sum + (g.pending_commitments || 0), 0);

    return {
      totalGyms,
      p1Filled, p2Filled, p3Filled, p4Filled,
      pendingVerifications,
      pendingCommitments
    };
  }, [sortedData]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pulseResponse, metricsResponse] = await Promise.all([
        axiosInstance.get(`/pulse-operations?date=${selectedDate}`),
        axiosInstance.get(`/command-center?date=${selectedDate}`)
      ]);
      setData(pulseResponse.data);
      const glance = metricsResponse.data?.today_at_glance || {};
      setKeyMetrics({
        visit_scheduled: glance.visit_scheduled || 0,
        visit_done: glance.visit_done || 0,
        closure_expected: glance.closure_expected || 0,
        closure_done: glance.closure_done || 0
      });
      setLastUpdate(new Date());
    } catch (error) {
      toast.error('Failed to load pulse operations');
      console.error('Error fetching pulse operations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh every 2 minutes (120 seconds)
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 120000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  const getPulseBadge = (pulseName, status, verified, fd) => {
    let badgeVariant = "outline";
    let badgeClass = "bg-gray-50 text-gray-600 border-gray-300";
    let badgeText = `${pulseName}: Missing`;

    if (status === 'filled') {
      if (verified) {
        badgeClass = "bg-green-100 text-green-700 border-green-300";
        badgeText = `${pulseName}: Verified`;
      } else {
        badgeClass = "bg-yellow-100 text-yellow-700 border-yellow-300";
        badgeText = `${pulseName}: Filled`;
      }
    }

    return (
      <div className="flex flex-col items-center gap-1">
        <Badge variant={badgeVariant} className={badgeClass}>
          {badgeText}
        </Badge>
        {fd !== null && fd !== undefined && (
          <span className="text-xs text-gray-500">FD: {fd}</span>
        )}
      </div>
    );
  };

  return (
    <div data-testid="pulse-operations-page">
      {/* Header with Title and Controls */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1F4E79] flex items-center gap-2">
            <Activity size={28} />
            PULSE OPERATIONS DASHBOARD
          </h1>
          <p className="text-sm text-gray-500">
            Last Updated: <span className="font-semibold">{lastUpdate.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })}</span> |
            Auto-refresh: 2 minutes
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-gray-500" />
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-40"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Sales Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-blue-600">{keyMetrics.visit_scheduled}</p>
            <p className="text-xs text-muted-foreground">Visit Scheduled</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-green-600">{keyMetrics.visit_done}</p>
            <p className="text-xs text-muted-foreground">Visit Done</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-purple-600">{keyMetrics.closure_expected}</p>
            <p className="text-xs text-muted-foreground">Closure Expected</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-emerald-600">{keyMetrics.closure_done}</p>
            <p className="text-xs text-muted-foreground">Closure Done</p>
          </CardContent>
        </Card>
      </div>

      {/* Summary Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <Card
          data-testid="metric-card-p1"
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => {
            setSelectedPulse('P1');
            setPulseModalOpen(true);
          }}
        >
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-[#1F4E79]">{summaryMetrics.p1Filled}/{summaryMetrics.totalGyms}</p>
            <p className="text-xs text-muted-foreground">P1 Filled</p>
          </CardContent>
        </Card>

        <Card
          data-testid="metric-card-p2"
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => {
            setSelectedPulse('P2');
            setPulseModalOpen(true);
          }}
        >
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-[#1F4E79]">{summaryMetrics.p2Filled}/{summaryMetrics.totalGyms}</p>
            <p className="text-xs text-muted-foreground">P2 Filled</p>
          </CardContent>
        </Card>

        <Card
          data-testid="metric-card-p3"
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => {
            setSelectedPulse('P3');
            setPulseModalOpen(true);
          }}
        >
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-[#1F4E79]">{summaryMetrics.p3Filled}/{summaryMetrics.totalGyms}</p>
            <p className="text-xs text-muted-foreground">P3 Filled</p>
          </CardContent>
        </Card>

        <Card
          data-testid="metric-card-p4"
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => {
            setSelectedPulse('P4');
            setPulseModalOpen(true);
          }}
        >
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-[#1F4E79]">{summaryMetrics.p4Filled}/{summaryMetrics.totalGyms}</p>
            <p className="text-xs text-muted-foreground">P4 Filled</p>
          </CardContent>
        </Card>

        <Card
          data-testid="metric-card-verifications"
          className="cursor-pointer hover:border-yellow-500 transition-colors"
          onClick={() => setDrilldownMetric({
            type: 'verifications',
            title: 'Pending Verifications',
            gyms: sortedData.filter(g => (g.pending_verifications || 0) > 0)
          })}
        >
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{summaryMetrics.pendingVerifications}</p>
            <p className="text-xs text-muted-foreground">Pending Verifications</p>
          </CardContent>
        </Card>

        <Card
          data-testid="metric-card-commitments"
          className="cursor-pointer hover:border-yellow-500 transition-colors"
          onClick={() => setDrilldownMetric({
            type: 'commitments',
            title: 'Pending Commitments',
            gyms: sortedData.filter(g => (g.pending_commitments || 0) > 0)
          })}
        >
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{summaryMetrics.pendingCommitments}</p>
            <p className="text-xs text-muted-foreground">Pending Commitments</p>
          </CardContent>
        </Card>
      </div>

      {/* Missing/Unfilled Drill-down Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card
          data-testid="metric-card-p1-missing"
          className="cursor-pointer hover:shadow-lg transition-shadow border-dashed"
          onClick={() => {
            setSelectedPulse('P1');
            setPulseModalOpen(true);
          }}
        >
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-red-500">{summaryMetrics.totalGyms - summaryMetrics.p1Filled}</p>
            <p className="text-xs text-muted-foreground">P1 Missing</p>
          </CardContent>
        </Card>

        <Card
          data-testid="metric-card-p2-missing"
          className="cursor-pointer hover:shadow-lg transition-shadow border-dashed"
          onClick={() => {
            setSelectedPulse('P2');
            setPulseModalOpen(true);
          }}
        >
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-red-500">{summaryMetrics.totalGyms - summaryMetrics.p2Filled}</p>
            <p className="text-xs text-muted-foreground">P2 Missing</p>
          </CardContent>
        </Card>

        <Card
          data-testid="metric-card-p3-missing"
          className="cursor-pointer hover:shadow-lg transition-shadow border-dashed"
          onClick={() => {
            setSelectedPulse('P3');
            setPulseModalOpen(true);
          }}
        >
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-red-500">{summaryMetrics.totalGyms - summaryMetrics.p3Filled}</p>
            <p className="text-xs text-muted-foreground">P3 Missing</p>
          </CardContent>
        </Card>

        <Card
          data-testid="metric-card-p4-missing"
          className="cursor-pointer hover:shadow-lg transition-shadow border-dashed"
          onClick={() => {
            setSelectedPulse('P4');
            setPulseModalOpen(true);
          }}
        >
          <CardContent className="pt-4 text-center">
            <p className="text-2xl font-bold text-red-500">{summaryMetrics.totalGyms - summaryMetrics.p4Filled}</p>
            <p className="text-xs text-muted-foreground">P4 Missing</p>
          </CardContent>
        </Card>
      </div>

      {/* Saved Filter Views */}
      <SavedFilterViews
        currentFilters={filters}
        onLoadFilter={(loadedFilters) => setFilters(loadedFilters)}
      />

      {/* Filter Bar */}
      <FilterBar filters={filters} setFilters={setFilters} options={filterOptions} />

      {/* Operations Table */}
      <Card>
        <CardHeader className="py-3 bg-[#1F4E79] text-white rounded-t-lg">
          <CardTitle className="text-base flex items-center justify-between">
            <span>PULSE STATUS BY GYM ({sortedData.length} gyms)</span>
            <span className="text-xs font-normal">
              Green: Verified | Yellow: Filled | Gray: Missing
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#D6DCE5]">
                  <TableHead className="font-bold text-gray-900 text-center w-12">Status</TableHead>
                  <TableHead className="font-bold text-gray-900">Gym</TableHead>
                  <TableHead className="font-bold text-gray-900">TL</TableHead>
                  <TableHead className="font-bold text-gray-900">Manager</TableHead>
                  <TableHead className="font-bold text-gray-900 text-center">P1</TableHead>
                  <TableHead className="font-bold text-gray-900 text-center">P2</TableHead>
                  <TableHead className="font-bold text-gray-900 text-center">P3</TableHead>
                  <TableHead className="font-bold text-gray-900 text-center">P4</TableHead>
                  <TableHead className="font-bold text-gray-900 text-center">FD Avg</TableHead>
                  <TableHead className="font-bold text-gray-900 text-center">Verifications</TableHead>
                  <TableHead className="font-bold text-gray-900 text-center">Commitments</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && sortedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <RefreshCw size={16} className="animate-spin" />
                        Loading pulse operations...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : sortedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-gray-500">
                      {assignedGyms.length === 0
                        ? `No pulse data available for ${selectedDate}`
                        : `No gyms match your filters. Try selecting different options or clear all filters.`
                      }
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedData.map((gym, idx) => {
                    const hasProblems = gym.indicators?.problems?.length > 0;
                    const hasExcellent = gym.indicators?.excellent?.length > 0 && !hasProblems;

                    return (
                      <TableRow
                        key={gym.gym_id}
                        className={cn(
                          idx % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                          hasProblems && 'bg-red-50 hover:bg-red-100',
                          hasExcellent && 'bg-green-50 hover:bg-green-100'
                        )}
                      >
                        <TableCell className="text-center">
                          <PerformanceIndicators indicators={gym.indicators} />
                        </TableCell>
                        <TableCell className="font-semibold text-blue-600">
                          <button
                            onClick={() => {
                              setSelectedGym(gym);
                              setDrilldownOpen(true);
                            }}
                            className="text-primary hover:underline cursor-pointer"
                          >
                            {gym.gym_name}
                          </button>
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {gym.tl_name || '-'}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {gym.manager_name || '-'}
                        </TableCell>
                        <TableCell className="text-center border-l">
                          {getPulseBadge('P1', gym.p1_status, gym.p1_verified, gym.p1_fd)}
                        </TableCell>
                        <TableCell className="text-center">
                          {getPulseBadge('P2', gym.p2_status, gym.p2_verified, gym.p2_fd)}
                        </TableCell>
                        <TableCell className="text-center">
                          {getPulseBadge('P3', gym.p3_status, gym.p3_verified, gym.p3_fd)}
                        </TableCell>
                        <TableCell className="text-center border-r">
                          {getPulseBadge('P4', gym.p4_status, gym.p4_verified, gym.p4_fd)}
                        </TableCell>
                        <TableCell className="text-center font-medium">
                          {gym.fd_avg !== null ? gym.fd_avg : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`font-medium ${gym.pending_verifications > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>
                            {gym.pending_verifications}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`font-medium ${gym.pending_commitments > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>
                            {gym.pending_commitments}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Legend Card */}
      <Card className="mt-6">
        <CardHeader className="py-3 bg-gray-50">
          <CardTitle className="text-sm">Legend</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">Verified</Badge>
              <span>Pulse filled and verified by both TL and Manager</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-yellow-100 text-yellow-700 border-yellow-300">Filled</Badge>
              <span>Pulse filled but awaiting manager verification</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-300">Missing</Badge>
              <span>Pulse not yet submitted</span>
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-500">
            <p><strong>FD:</strong> Front Desk availability for the pulse</p>
            <p><strong>FD Avg:</strong> Average FD availability across all pulses for the day</p>
            <p><strong>Verifications:</strong> Number of pulses awaiting manager verification</p>
            <p><strong>Commitments:</strong> Number of pending commitments (closure expected but not fulfilled)</p>
          </div>
        </CardContent>
      </Card>

      {/* Drill-down Dialog */}
      <Dialog open={!!drilldownMetric} onOpenChange={() => setDrilldownMetric(null)}>
        <DialogContent data-testid="drilldown-dialog" className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{drilldownMetric?.title} ({drilldownMetric?.gyms?.length} gyms)</DialogTitle>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Gym</TableHead>
                <TableHead>TL</TableHead>
                <TableHead>Manager</TableHead>
                <TableHead className="text-center">FD Avg</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {drilldownMetric?.gyms?.map((gym, idx) => (
                <TableRow key={gym.gym_id || idx}>
                  <TableCell className="font-semibold">{gym.gym_name}</TableCell>
                  <TableCell>{gym.tl_name || '-'}</TableCell>
                  <TableCell>{gym.manager_name || '-'}</TableCell>
                  <TableCell className="text-center">{gym.fd_avg ?? '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {drilldownMetric?.gyms?.length === 0 && (
            <p className="text-center py-4 text-gray-500">No gyms match this criteria</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Pulse Breakdown Modal */}
      <PulseBreakdownModal
        pulse={selectedPulse}
        gyms={sortedData}
        open={pulseModalOpen}
        selectedDate={selectedDate}
        onClose={() => {
          setPulseModalOpen(false);
          setSelectedPulse(null);
        }}
      />

      {/* Gym Drilldown Modal */}
      <GymDrilldownModal
        gymId={selectedGym?.gym_id}
        date={selectedDate}
        open={drilldownOpen}
        onClose={() => {
          setDrilldownOpen(false);
          setSelectedGym(null);
        }}
      />
    </div>
  );
}
